import { Autocomplete, Box, Stack, TextField } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import ActionButton from "components/Buttons/ActionButton";
import SaveButton from "components/Buttons/SaveButton";
import CharsLimitedTextField from "components/CharsLimitedTextField";
import QUESTION_TYPE from "enums/QUESTION_TYPE";
import { Fragment, useEffect, useMemo, useState } from "react";
import QuestionDto from "types/QuestionDto";
import { moveArrayIndex, removeFromArray } from "utils/array";
import useScriptManagementStore from "../../hooks/useScriptManagementStore";
import useFormHelpers from "../hooks/useFormHelpers";
import OptionDropZone from "./OptionDropZone";
import QuestionOption, { OPTION_DELIMITER } from "./QuestionOption";

interface Props {
  question: QuestionDto;
  index: number;
}

const MAX_CHARS = 255;

export default function FormQuestion({ question, index }: Props) {
  const { isOptionType, questionTypes } = useFormHelpers();
  const {
    handleUpdateQuestion,
    handleDeleteQuestion,
    handleStoreChange,
    isEditing,
  } = useScriptManagementStore();
  const [questionType, setQuestionType] = useState<QUESTION_TYPE>(
    question.type,
  );
  const [questionTitle, setQuestionTitle] = useState<string>(question.title);
  const [optionTitle, setOptionTitle] = useState<string>("");
  const [questionOptions, setQuestionOptions] = useState<string[]>(
    question?.options || [],
  );

  const typeValue = useMemo(
    () => questionTypes.find((i) => i.id === questionType),
    [questionType],
  );

  useEffect(() => {
    if (!isOptionType(questionType)) {
      setQuestionOptions([]);
    }
  }, [questionType]);

  function deleteOption(index: number) {
    setQuestionOptions(removeFromArray([...questionOptions], index));
  }

  function editOption(option: string, index: number) {
    const newOptions = [...questionOptions];
    newOptions[index] = option;
    setQuestionOptions(newOptions);
  }

  function updateOptions(payload) {
    const { index, hoverIndex } = payload;
    setQuestionOptions(moveArrayIndex(questionOptions, index, hoverIndex || 0));
  }

  function updateQuestion() {
    const payload: QuestionDto = {
      title: questionTitle,
      type: questionType,
    };
    if (isOptionType(questionType)) {
      payload.options = questionOptions;
    }
    handleUpdateQuestion(payload, index);
    handleStoreChange("isEditing", false);
  }

  function deleteQuestion() {
    handleDeleteQuestion(index);
  }

  function cancelEditQuestion() {
    handleStoreChange("editIndex", -1);
    handleStoreChange("isEditing", false);
  }

  function saveDisabled() {
    return (
      !questionTitle ||
      questionTitle.length > MAX_CHARS ||
      ((questionType === QUESTION_TYPE.SINGLE_CHOICE ||
        questionType === QUESTION_TYPE.MULTIPLE_CHOICE) &&
        questionOptions.length === 0)
    );
  }

  return (
    <Box
      sx={({ palette, borders, functions }) => ({
        backgroundColor: palette.grey[300],
        borderRadius: borders.borderRadius.md,
        p: 1,
        mb: functions.pxToRem(16),
      })}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Autocomplete
            options={questionTypes}
            onChange={(event, value) => {
              setQuestionType(value.id);
            }}
            value={typeValue}
            disableClearable
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: (theme) => ({
                    top: -12,
                    left: -12,
                    ...theme.typography.h6,
                  }),
                }}
                sx={({ typography }) => ({
                  legend: { display: "none" },
                  fieldset: { top: 0 },
                  marginTop: typography.pxToRem(20),
                })}
                label="Question Type"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <CharsLimitedTextField
            value={questionTitle}
            onChange={(event) => {
              setQuestionTitle(event.target.value);
            }}
            label="Title *"
            maxChars={MAX_CHARS}
          />
        </Grid>
        {questionOptions.length > 0 && (
          <Grid item xs={12}>
            <Box pl={4} sx={{ width: "100%" }}>
              {questionOptions.map((option: string, index: number) => (
                <Fragment key={option + index}>
                  <OptionDropZone index={index} onDrop={updateOptions} />
                  <QuestionOption
                    option={option}
                    index={index}
                    onDelete={deleteOption}
                    onEdit={editOption}
                  />
                </Fragment>
              ))}

              <OptionDropZone index={index + 1} onDrop={updateOptions} />
            </Box>
          </Grid>
        )}
        {isOptionType(questionType) && (
          <Grid item xs={12}>
            <Grid item xs={12}>
              <Stack direction="row" sx={{ width: "100%", pl: 4 }}>
                <CharsLimitedTextField
                  value={optionTitle}
                  label="Answer *"
                  maxChars={MAX_CHARS}
                  onChange={(event) => {
                    setOptionTitle(
                      event.target.value.replaceAll(OPTION_DELIMITER, ":"),
                    );
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter" && optionTitle !== "") {
                      setQuestionOptions([...questionOptions, optionTitle]);
                      setOptionTitle("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value) {
                      setQuestionOptions([...questionOptions, optionTitle]);
                      setOptionTitle("");
                    }
                  }}
                />
                <SaveButton
                  disabled={
                    optionTitle.trim() === "" || optionTitle.length > MAX_CHARS
                  }
                  onClick={() => {
                    setQuestionOptions([...questionOptions, optionTitle]);
                    setOptionTitle("");
                  }}
                  sx={{ height: "2rem", alignSelf: "center", ml: 2 }}
                >
                  Save Answer
                </SaveButton>
              </Stack>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Stack direction="row" spacing={1}>
            <SaveButton disabled={saveDisabled()} onClick={updateQuestion}>
              Save Question
            </SaveButton>
            <ActionButton
              onClick={isEditing ? cancelEditQuestion : deleteQuestion}
            >
              CANCEL
            </ActionButton>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
